// =====================
// Invoice Approval Requests (client-portal-financials Phase 2, C4)
// =====================
//
// Per-invoice client approval before send. One request row per submission
// round: staff submit, portal users decide. Events are append-only
// decision/comment rows and request status is DERIVED from them in one API
// service function (latest decision event wins; COMMENT events never change
// status). SUPERSEDED is applied by the supersede operations (a re-request,
// or a material invoice edit after approval), never derived from events.
// Deliberately NOT an InvoiceStatus: approval is workflow truth in this
// object and SEND is the enforcement point. Same lineage as
// contract-approval.ts and budget-change.ts — one approval idiom across the
// codebase, separate tables per object. Unlike those, events have NO
// unique-per-actor constraint: a client can comment, see a revision, and
// then approve on the same round.

import { z } from 'zod'

export type InvoiceApprovalRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'CHANGES_REQUESTED'
  | 'CANCELLED'
  | 'SUPERSEDED'

export type InvoiceApprovalEventType =
  | 'APPROVED'
  | 'CHANGES_REQUESTED'
  | 'COMMENT'
  | 'CANCELLED'

export type InvoiceApprovalDecision = 'APPROVED' | 'CHANGES_REQUESTED'

// ---- Request bodies ----

export const SubmitInvoiceApprovalSchema = z.object({
  invoiceId: z.string().uuid(),
})

export type SubmitInvoiceApprovalInput = z.infer<
  typeof SubmitInvoiceApprovalSchema
>

// Approve: comment optional. Request-changes: the comment IS the change
// request, so it is required there (enforced by the portal endpoint schema
// below rather than a union here).
export const ApproveInvoiceApprovalSchema = z.object({
  comment: z.string().max(2000).optional(),
})

export type ApproveInvoiceApprovalInput = z.infer<
  typeof ApproveInvoiceApprovalSchema
>

export const RequestInvoiceChangesSchema = z.object({
  comment: z.string().trim().min(1, 'Describe the changes you need').max(2000),
})

export type RequestInvoiceChangesInput = z.infer<
  typeof RequestInvoiceChangesSchema
>

export const CommentInvoiceApprovalSchema = z.object({
  comment: z.string().trim().min(1, 'Comment is required').max(2000),
})

export type CommentInvoiceApprovalInput = z.infer<
  typeof CommentInvoiceApprovalSchema
>

// ---- Responses ----

export interface InvoiceApprovalEvent {
  id: string
  actorId: string
  actorName: string | null
  /** True when the actor is an org member (staff), false for portal users. */
  isStaffActor: boolean
  type: InvoiceApprovalEventType
  comment: string | null
  createdAt: string
}

export interface InvoiceApprovalRequestWithTrail {
  id: string
  organizationId: string
  invoiceId: string
  status: InvoiceApprovalRequestStatus
  requestedBy: string
  requestedByName: string | null
  supersededById: string | null
  createdAt: string
  updatedAt: string
  events: InvoiceApprovalEvent[]
}

// Portal pending-approval card: the whitelisted invoice summary a client sees
// for a DRAFT awaiting their decision (the one place a draft is portal
// visible). Line summary mirrors what the sent PDF would show; nothing
// internal (authorization linkage, staff notes, settings blob) transits.
export interface PortalInvoiceApprovalLineItem {
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface PortalInvoiceApproval {
  request: InvoiceApprovalRequestWithTrail
  invoice: {
    id: string
    invoiceNumber: string
    title: string
    issueDate: string
    dueDate: string
    currency: string
    totalAmount: number
    lineItems: PortalInvoiceApprovalLineItem[]
  }
}
